import { createSandboxStorage, type StorageLike } from '../services/sandboxStorage';
import { createSyncedStorage, notifySavedKeyWrite } from '../account/saveSync';
import { isDebugEnabled } from './debugMode';

/**
 * The store every save-data service reads and writes: real `localStorage` in
 * normal play, a throwaway copy-on-write view over it under `?debug`.
 *
 * Debug mode exists to reach any screen in any state quickly — PHANTOM UNLOCK
 * and UNLOCK CH2 hand you towers and chapters outright — and none of that is
 * meant to become real progress. Reads still fall through, so a debug session
 * opens on your actual save; writes stop at the sandbox and are gone on reload.
 *
 * One instance per page load, shared by every scene, so a grant made on Level
 * Select is still owned in Equip and in the episode itself.
 *
 * Audio settings deliberately do NOT go through here — volume is a device
 * preference, not save data, and it should stick however you set it.
 *
 * Normal play returns a SYNCED view: writes to account-portable keys nudge
 * the save-sync engine (a no-op for guests and unconfigured builds). The
 * ?debug sandbox wraps PLAIN localStorage — debug writes must never sync.
 */
let sandbox: StorageLike | null = null;
let synced: StorageLike | null = null;

export function saveStorage(): StorageLike {
  if (isDebugEnabled()) {
    sandbox ??= createSandboxStorage(window.localStorage);
    return sandbox;
  }
  synced ??= createSyncedStorage(window.localStorage, notifySavedKeyWrite);
  return synced;
}
